import { Tab, Tabs } from '@mui/material';
import { HashRouter } from 'react-router-dom';
import DataGridToolBar from './Headers/HeaderGrid/DataGridToolBar';
import { PageType, tableContext } from './Headers/HeaderGrid/useTableContext';
import TabsRouter from './router/TabsRouter';

import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useLastLocation from './hooks/useLastLocation';
import useTabNavigation from './hooks/useTabNavigation';

import { StyledStickyPaper } from 'components/shared/Paper/StyledStickyPaper';
import StyledTabs from 'components/shared/StyledTabs';

import type { FC } from 'react';
import type { Header, TabType } from './types';

interface Props {
  page: string;
  /** Grant-filtered and sorted, and guaranteed to hold at least one header. */
  headers: Header[];
  /** Grant-filtered and sorted: the views that get a tab and a route. */
  views: TabType[];
  /** The unfiltered config, only needed to tell a removed view from a hidden one. */
  configuredViews: TabType[];
  userGrantNeeded: string;
}

const TabsView: FC<Props> = ({
  page,
  headers,
  views,
  configuredViews,
  userGrantNeeded
}) => {
  'use memo';

  const { t } = useTranslation('common');
  const [pageType, setPageType] = useState<PageType>(PageType.LIST);
  const portalTargetRef = useRef<HTMLDivElement>(null);

  // Indexing without a guard is sound here: the caller filters the headers and
  // renders a placeholder instead of this component when none survive.
  const { tabIndex, setTabIndex, tabbedElement, setTabbedElement } =
    useTabNavigation(0, headers[0].tabs[0], headers, views, configuredViews);

  // After `useTabNavigation` on purpose: both write a `page` param in the same
  // commit and the restore inside this hook has to be the one that lands.
  useLastLocation(tabbedElement, tabIndex, headers);

  const handleChange = (e) => {
    setTabIndex(e.target.tabIndex);
    setTabbedElement(headers[e.target.tabIndex].tabs[0]);
  };

  // `tabIndex` stays -1 for as long as the active view has no header in `headers`.
  // The view filter in `useVisibleTabs` closes the routes that could get us there;
  // this keeps a future one from taking the whole screen down with it.
  const activeHeader = headers[tabIndex] ?? headers[0];

  return (
    <StyledTabs className="tabs">
      <StyledStickyPaper square>
        <Tabs
          variant="scrollable"
          allowScrollButtonsMobile
          value={tabIndex}
          indicatorColor="primary"
          textColor="primary"
          onChange={handleChange}
        >
          {/* No second `requiredGrants` filter here: `useVisibleTabs` already
              applied it, and filtering again would shift these indexes out of step
              with the `tabIndex` that `value` and `handleChange` count on. */}
          {headers.map((header, index) => (
            <Tab
              key={header.subHeaderName}
              component="a"
              tabIndex={index}
              label={t(`tableHeaders.${header.subHeaderName}`)}
              href={`/#${page}&page=${header.tabs[0].name.toLowerCase()}`}
              onClick={() => setPageType(PageType.LIST)}
              sx={{
                textTransform: 'none'
              }}
            />
          ))}
        </Tabs>
      </StyledStickyPaper>
      <div style={{ height: '100%', padding: 20 }}>
        <HashRouter hashType="noslash" baseName={'/'}>
          <tableContext.Provider
            value={{
              contentNodeForButtons: portalTargetRef,
              context: {
                page: page,
                type: pageType,
                setPageType: setPageType
              }
            }}
          >
            <DataGridToolBar
              setTabbedElement={setTabbedElement}
              tabbedElement={tabbedElement}
              tabulatedItems={activeHeader.tabs}
              page={page}
            />
            <TabsRouter
              page={page}
              views={views}
              requiredGrants={userGrantNeeded}
              fallbackView={tabbedElement}
            />
          </tableContext.Provider>
        </HashRouter>
      </div>
    </StyledTabs>
  );
};

export default TabsView;
